/**
 * Web hooks for billing settings
 * Using React hooks (useState/useEffect) - consistent with other web hooks
 */

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface BillingSettings {
  id: string;
  company_id: string;
  payment_provider?: 'rollops' | 'self-managed';
  billing_strategy: 'monthly_fixed_date' | 'on_signup';
  fixed_billing_day: number | null;
  late_fee_strategy: 'staged' | 'max_total';
  late_fee_config: Array<{ days: number; amount: number }> | { max_amount: number; days_until_cancellation: number };
  cancellation_notice_days: number;
  additional_terms_and_conditions?: string | null;
  platform_fee_percentage?: number | null;
  platform_fee_amount?: number | null;
  auto_cancellation_enabled?: boolean;
  auto_cancellation_days_overdue?: number | null;
  fee_schedule_id?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Hook: Get billing settings for a company
 */
export function useBillingSettings(companyId: string | null | undefined) {
  const [data, setData] = useState<BillingSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!companyId) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    (async () => {
      try {
        const { data: settingsData, error: settingsError } = await supabase
          .from('subscription_billing_settings')
          .select('*')
          .eq('company_id', companyId)
          .single();
        
        if (settingsError) {
          // PGRST116 = no rows returned (no settings exist yet)
          // PGRST301 = JWT role claim required (RLS blocking - likely JWT not refreshed)
          // 406 = Not Acceptable (similar to PGRST116 but from HTTP layer)
          const isNoData = settingsError.code === 'PGRST116' || settingsError.code === '406';
          
          if (isNoData) {
            // No settings exist yet, return default values
            setData({
              id: '',
              company_id: companyId,
              payment_provider: 'rollops',
              billing_strategy: 'monthly_fixed_date',
              fixed_billing_day: 1,
              late_fee_strategy: 'staged',
              late_fee_config: [],
              cancellation_notice_days: 30,
              auto_cancellation_enabled: false,
              auto_cancellation_days_overdue: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            } as BillingSettings);
          } else {
            console.error('[useBillingSettings] Error fetching settings:', settingsError);
            setError(settingsError instanceof Error ? settingsError : new Error(String(settingsError)));
          }
        } else {
          // late_fee_config is Json in DB but structured in BillingSettings
          setData(settingsData as unknown as BillingSettings);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [companyId]);

  return { data, isLoading, error };
}

/**
 * Hook: Update billing settings
 */
export function useUpdateBillingSettings() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const update = useCallback(async (input: {
    companyId: string;
    payment_provider?: 'rollops' | 'self-managed';
    billing_strategy?: 'monthly_fixed_date' | 'on_signup';
    fixed_billing_day?: number | null;
    late_fee_strategy?: 'staged' | 'max_total';
    late_fee_config?: Array<{ days: number; amount: number }> | { max_amount: number; days_until_cancellation: number };
    cancellation_notice_days?: number;
    additional_terms_and_conditions?: string | null;
    platform_fee_percentage?: number | null;
    platform_fee_amount?: number | null;
    auto_cancellation_enabled?: boolean;
    auto_cancellation_days_overdue?: number | null;
  }) => {
    setIsLoading(true);
    setError(null);

    try {
      const { companyId, ...updates } = input;

      // Remove undefined values to allow partial updates
      const dbUpdates: Record<string, unknown> = {
        ...updates,
        updated_at: new Date().toISOString(),
      };

      // Remove undefined values to allow partial updates
      Object.keys(dbUpdates).forEach(key => {
        if (dbUpdates[key] === undefined) {
          delete dbUpdates[key];
        }
      });

      // Check if settings exist and get current values for constraint validation
      const { data: existing } = await supabase
        .from('subscription_billing_settings')
        .select('billing_strategy, fixed_billing_day')
        .eq('company_id', companyId)
        .single();

      if (existing) {
        // Handle constraint: billing_strategy and fixed_billing_day must be consistent
        // If billing_strategy = 'monthly_fixed_date', fixed_billing_day must NOT be NULL
        // If billing_strategy = 'on_signup', fixed_billing_day must be NULL
        
        const newBillingStrategy = dbUpdates.billing_strategy as string | undefined;
        const newFixedBillingDay = dbUpdates.fixed_billing_day as number | null | undefined;
        
        // If user is explicitly updating fixed_billing_day, respect that and adjust billing_strategy if needed
        if (newFixedBillingDay !== undefined) {
          if (newFixedBillingDay === null) {
            // User wants fixed_billing_day = null, so billing_strategy must be 'on_signup'
            dbUpdates.billing_strategy = 'on_signup';
            dbUpdates.fixed_billing_day = null;
          } else {
            // User wants fixed_billing_day = value, so billing_strategy must be 'monthly_fixed_date'
            dbUpdates.billing_strategy = 'monthly_fixed_date';
            dbUpdates.fixed_billing_day = newFixedBillingDay;
          }
        } else if (newBillingStrategy !== undefined) {
          // User is updating billing_strategy, ensure fixed_billing_day is consistent
          if (newBillingStrategy === 'monthly_fixed_date') {
            // Must have a fixed_billing_day value (NOT NULL)
            // Use existing value if available and valid, otherwise default to 1
            dbUpdates.fixed_billing_day = (existing.fixed_billing_day !== null && existing.fixed_billing_day !== undefined) 
              ? existing.fixed_billing_day 
              : 1;
          } else if (newBillingStrategy === 'on_signup') {
            // Must have fixed_billing_day as NULL
            dbUpdates.fixed_billing_day = null;
          }
        }
        
        // Update existing - only send defined fields
        const { data, error: updateError } = await supabase
          .from('subscription_billing_settings')
          .update(dbUpdates)
          .eq('company_id', companyId)
          .select()
          .single();

        if (updateError) {
          console.error('[useUpdateBillingSettings] Update error:', updateError);
          throw new Error(updateError.message || 'Failed to update billing settings');
        }
        
        // late_fee_config is Json type from database, needs proper type assertion
        return data as unknown as BillingSettings;
      } else {
        // Create new - ensure required fields have defaults
        const insertData: Record<string, unknown> = {
          company_id: companyId,
          payment_provider: updates.payment_provider || 'rollops', // Required field
          billing_strategy: updates.billing_strategy || 'monthly_fixed_date',
          fixed_billing_day: updates.fixed_billing_day ?? 1,
          late_fee_strategy: updates.late_fee_strategy || 'staged',
          late_fee_config: updates.late_fee_config || [],
          cancellation_notice_days: updates.cancellation_notice_days ?? 30,
          additional_terms_and_conditions: updates.additional_terms_and_conditions || null,
        };

        // Include optional fields if provided
        if (updates.platform_fee_percentage !== undefined) {
          insertData.platform_fee_percentage = updates.platform_fee_percentage;
        }
        if (updates.platform_fee_amount !== undefined) {
          insertData.platform_fee_amount = updates.platform_fee_amount;
        }
        if (updates.auto_cancellation_enabled !== undefined) {
          insertData.auto_cancellation_enabled = updates.auto_cancellation_enabled;
        }
        if (updates.auto_cancellation_days_overdue !== undefined) {
          insertData.auto_cancellation_days_overdue = updates.auto_cancellation_days_overdue;
        }
        
        const { data, error: insertError } = await supabase
          .from('subscription_billing_settings')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamically constructed insert object
          .insert(insertData as any)
          .select()
          .single();

        if (insertError) {
          console.error('[useUpdateBillingSettings] Insert error:', insertError);
          throw new Error(insertError.message || 'Failed to create billing settings');
        }
        
        // late_fee_config is Json type from database, needs proper type assertion
        return data as unknown as BillingSettings;
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { update, isLoading, error };
}

